import type { TickerMatch, TickerSnapshot, WidgetConfig } from "./types";
import { css } from "./styles";
import { fetchSnapshot } from "./api";
import { buildBar, fillCards, type BarRefs } from "./render";
import { enableScrolling, type ScrollControl } from "./drag";
import { strings, formatKickoff } from "./i18n";

const MIN_POLL = 15; // never poll faster than this, regardless of backend ttl
const MAX_POLL = 60; // never wait longer than this, so kickoffs/scores show up

/** "Julián Quiñones" -> "J. Quiñones"; leaves single-token names untouched. */
function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
}

/** First half = minute 1–45 (incl. stoppage like "45+2"). */
function isFirstHalf(minute: string): boolean {
  const n = parseInt(minute, 10);
  return Number.isNaN(n) ? true : n <= 45;
}

/** Small flag <img> for a goal row, or null when no flag URL is known. */
function goalFlag(url: string, code: string): HTMLImageElement | null {
  if (!url) return null;
  const img = document.createElement("img");
  img.className = "wc-goal-flag";
  img.src = url;
  img.alt = code;
  img.loading = "lazy";
  img.decoding = "async";
  img.addEventListener("error", () => {
    img.style.display = "none";
  });
  return img;
}

/** Read per-store config from the host element's data-* attributes. */
function readConfig(host: HTMLElement): WidgetConfig | null {
  const api = host.dataset.api?.trim();
  if (!api) {
    // Without a backend URL the widget can do nothing — fail silently.
    // eslint-disable-next-line no-console
    console.warn("[wc-ticker] missing data-api on", host);
    return null;
  }
  const position = host.dataset.position === "static" ? "static" : "top";
  return {
    api,
    lang: host.dataset.lang?.trim() || "pt",
    position,
    accent: host.dataset.accent?.trim() || "#16a34a",
    // Auto-scroll is ON by default (cards drift on their own, pausing on hover);
    // set data-autoscroll="false" to disable.
    autoscroll: !/^(0|false|no|off)$/i.test(host.dataset.autoscroll || ""),
    expandable: !/^(0|false|no|off)$/i.test(host.dataset.expandable || ""),
    title: host.dataset.title?.trim() || "", // "" -> localized default
    logo: host.dataset.logo?.trim() || "",
    headerBg: host.dataset.headerBg?.trim() || "#ffffff",
  };
}

/** Pick a readable text color (dark/light) for a given background hex. */
function contrastColor(hex: string): string {
  const m = hex.replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "#0b0f14";
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.6 ? "#0b0f14" : "#ffffff";
}

class TickerWidget {
  private root: ShadowRoot;
  private wrapper: HTMLElement;
  private overlay!: HTMLElement;
  private overlayRoot!: ShadowRoot;
  private refs: BarRefs;
  private scroll: ScrollControl | null = null;
  private timer: number | null = null;
  private destroyed = false;
  private matches: TickerMatch[] = [];
  private openId: string | null = null;
  private onDocPointer: ((e: Event) => void) | null = null;
  private onKey: ((e: KeyboardEvent) => void) | null = null;
  private onWinScroll: (() => void) | null = null;

  constructor(private host: HTMLElement, private cfg: WidgetConfig) {
    this.root = host.attachShadow({ mode: "open" });

    const style = document.createElement("style");
    style.textContent = css(cfg.accent, cfg.headerBg, contrastColor(cfg.headerBg));
    this.root.appendChild(style);

    this.refs = buildBar(cfg);
    this.wrapper = document.createElement("div");
    this.wrapper.className = "wc-root";
    this.wrapper.append(this.refs.header, this.refs.bar);
    this.root.appendChild(this.wrapper);

    // The detail dropdown is rendered in a PORTAL at <body> level (its own
    // element + shadow root), positioned `fixed`. This escapes the theme's
    // ancestor stacking contexts and any `overflow:hidden` — otherwise the
    // section/header below can cover it no matter how high our z-index is.
    this.overlay = document.createElement("div");
    this.overlay.className = "wc-overlay";
    this.overlayRoot = this.overlay.attachShadow({ mode: "open" });
    const overlayStyle = document.createElement("style");
    overlayStyle.textContent = css(cfg.accent, cfg.headerBg, contrastColor(cfg.headerBg));
    this.overlayRoot.append(overlayStyle, this.refs.detail);
    Object.assign(this.overlay.style, {
      position: "fixed",
      top: "0",
      left: "0",
      zIndex: "2147483647",
      display: "none",
      margin: "0",
    });
    document.body.appendChild(this.overlay);

    this.scroll = enableScrolling(
      this.refs.scroller,
      this.refs.prev,
      this.refs.next,
      { autoscroll: cfg.autoscroll }
    );

    if (cfg.expandable) {
      // Listen on the whole bar so both pinned (live) and scrolling cards work.
      // drag.ts suppresses the click on the scroller after a drag (capture
      // phase), so dragging never accidentally opens a detail panel.
      this.refs.bar.addEventListener("click", (e) => this.onCardClick(e));

      // Close on outside click (not on the bar nor inside the dropdown) / Escape.
      this.onDocPointer = (e: Event) => {
        if (!this.openId) return;
        const path = e.composedPath();
        if (!path.includes(this.host) && !path.includes(this.overlay)) this.closeDetail();
      };
      this.onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape" && this.openId) this.closeDetail();
      };
      // Page scroll/resize would detach the fixed dropdown from the card -> close.
      this.onWinScroll = () => {
        if (this.openId) this.closeDetail();
      };
      document.addEventListener("pointerdown", this.onDocPointer);
      document.addEventListener("keydown", this.onKey);
      window.addEventListener("scroll", this.onWinScroll, { passive: true });
      window.addEventListener("resize", this.onWinScroll);
    }

    // Hidden until we have something to show — never disrupts the page on load.
    this.hide();
  }

  private applyHostLayout(): void {
    const s = this.host.style;
    s.display = "block";
    s.width = "100%";
    s.margin = "0"; // sit flush — no vertical gap from the host itself
    if (this.cfg.position === "top") {
      // Sticky keeps the bar pinned while still reserving space in flow, so it
      // pushes the page down instead of covering content. The dropdown lives in
      // a body-level portal, so it doesn't depend on this z-index.
      s.position = "sticky";
      s.top = "0";
      s.zIndex = "9999";
    }
  }

  private hide(): void {
    this.host.style.display = "none";
  }

  private onCardClick(e: Event): void {
    const target = e.target as HTMLElement;
    const card = target.closest(".wc-card") as HTMLElement | null;
    if (!card?.dataset.id) return;
    const id = card.dataset.id;
    if (this.openId === id) {
      this.closeDetail();
      return;
    }
    const m = this.matches.find((x) => x.id === id);
    if (!m) return;
    this.openDetail(card, m);
  }

  private openDetail(card: HTMLElement, m: TickerMatch): void {
    this.openId = m.id;
    this.renderDetail(m);
    this.overlay.style.display = "block";
    this.refs.detail.classList.add("wc-open");
    this.positionDetail(card);
    // Freeze the carousel while the dropdown is open so it stays aligned.
    this.scroll?.setPaused(true);
  }

  private closeDetail(): void {
    this.openId = null;
    this.refs.detail.classList.remove("wc-open");
    this.overlay.style.display = "none";
    this.scroll?.setPaused(false);
  }

  /**
   * Place the body-level dropdown (position: fixed) just below the bar, its
   * left edge under the clicked card, clamped to the viewport.
   */
  private positionDetail(card: HTMLElement): void {
    const barRect = this.refs.bar.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const width = this.refs.detail.offsetWidth || 380;
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.max(8, Math.min(cardRect.left, maxLeft));
    this.overlay.style.top = `${barRect.bottom}px`;
    this.overlay.style.left = `${left}px`;
  }

  private renderDetail(m: TickerMatch): void {
    const str = strings(this.cfg.lang);
    this.refs.detail.textContent = "";

    // Header: teams + score, and the state (minute / HT / FT / kickoff).
    let state: string;
    if (m.status === "LIVE") {
      state = `${str.live} ${m.minute != null ? m.minute + "'" : ""}`.trim();
    } else if (m.status === "HT") {
      state = str.ht;
    } else if (m.status === "UPCOMING") {
      state = m.startsAt ? formatKickoff(m.startsAt, this.cfg.lang) : "";
    } else {
      state = str.ft;
    }

    const head = document.createElement("div");
    head.className = "wc-detail-head";
    const teams = document.createElement("span");
    teams.className = "wc-detail-teams";
    teams.textContent =
      m.status === "UPCOMING"
        ? `${m.home.name} vs ${m.away.name}`
        : `${m.home.name} ${m.score.home} – ${m.score.away} ${m.away.name}`;
    const status = document.createElement("span");
    status.className = "wc-detail-status";
    status.textContent = [m.stage, state].filter(Boolean).join(" · ");
    head.append(teams, status);
    this.refs.detail.appendChild(head);

    // Scorers timeline for played matches (LIVE/HT/FT). Upcoming has none.
    if (m.status === "UPCOMING") return;

    const goals = m.goals || [];
    if (!goals.length) {
      const none = document.createElement("span");
      none.className = "wc-no-goals";
      none.textContent = str.noGoals;
      this.refs.detail.appendChild(none);
      return;
    }

    const list = document.createElement("ul");
    list.className = "wc-goals";
    let h = 0;
    let a = 0;
    let prevHalf: 1 | 2 | null = null;

    for (const g of goals) {
      const half: 1 | 2 = isFirstHalf(g.minute) ? 1 : 2;
      // Insert the half-time divider when crossing from a first- to a
      // second-half goal.
      if (prevHalf === 1 && half === 2) {
        const sep = document.createElement("li");
        sep.className = "wc-half-sep";
        sep.textContent = str.ht;
        list.appendChild(sep);
      }
      prevHalf = half;

      if (g.team === "home") h += 1;
      else a += 1;
      const scorer = g.team === "home" ? m.home : m.away;

      const li = document.createElement("li");
      li.className = "wc-goal" + (g.team === "away" ? " wc-goal-away" : "");

      const min = document.createElement("span");
      min.className = "wc-goal-min";
      min.textContent = g.minute ? `${g.minute}'` : "";

      const pill = document.createElement("span");
      pill.className = "wc-goal-pill";
      const ball = document.createElement("span");
      ball.className = "wc-goal-ball";
      ball.textContent = "⚽";
      ball.setAttribute("aria-hidden", "true");
      const score = document.createElement("span");
      score.textContent = `${h}-${a}`;
      pill.append(ball, score);

      const player = document.createElement("span");
      player.className = "wc-goal-player";
      const flag = goalFlag(scorer.flag, scorer.code);
      if (flag) player.appendChild(flag);
      player.appendChild(document.createTextNode(abbreviateName(g.player)));

      li.append(min, pill, player);
      list.appendChild(li);
    }
    this.refs.detail.appendChild(list);
  }

  private render(snapshot: TickerSnapshot): void {
    this.matches = snapshot.matches;
    if (!this.matches.length) {
      // Nothing live and nothing finished -> stay out of the way.
      this.closeDetail();
      this.hide();
      return;
    }
    fillCards(this.refs, this.matches, this.cfg);
    // Keep an open detail panel in sync, or close it if its match vanished.
    if (this.openId) {
      const m = this.matches.find((x) => x.id === this.openId);
      if (m) this.renderDetail(m);
      else this.closeDetail();
    }
    this.applyHostLayout();
  }

  private async tick(): Promise<void> {
    if (this.destroyed) return;
    let ttl = MIN_POLL;
    this.refs.bar.classList.add("wc-is-updating");
    try {
      const snap = await fetchSnapshot(this.cfg.api);
      ttl = Math.min(MAX_POLL, Math.max(MIN_POLL, snap.ttl || MIN_POLL));
      this.render(snap);
    } catch {
      // Backend down: if we've never shown anything, stay hidden; otherwise
      // keep the last good render on screen. Never break the page.
      if (!this.matches.length) this.hide();
    } finally {
      this.refs.bar.classList.remove("wc-is-updating");
      if (!this.destroyed) {
        this.timer = window.setTimeout(() => void this.tick(), ttl * 1000);
      }
    }
  }

  start(): void {
    void this.tick();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.timer) clearTimeout(this.timer);
    this.scroll?.cleanup();
    if (this.onDocPointer) document.removeEventListener("pointerdown", this.onDocPointer);
    if (this.onKey) document.removeEventListener("keydown", this.onKey);
    if (this.onWinScroll) {
      window.removeEventListener("scroll", this.onWinScroll);
      window.removeEventListener("resize", this.onWinScroll);
    }
    this.overlay?.remove();
  }
}

/** Boot every host element on the page (id or class), once each. */
function boot(): void {
  const hosts = document.querySelectorAll<HTMLElement>(
    "#wc-ticker, .wc-ticker"
  );
  hosts.forEach((host) => {
    if ((host as any).__wcTicker) return; // idempotent
    const cfg = readConfig(host);
    if (!cfg) return;
    const widget = new TickerWidget(host, cfg);
    (host as any).__wcTicker = widget;
    widget.start();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}

// Exposed for tests and manual re-init after dynamic DOM changes.
export { TickerWidget, readConfig, boot };
